// Phase 8 R2 — Canned Jobs management page (/canned-jobs).
// Owner / Manager only. Two-column layout: list left, editor right.

import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import {
  useCannedJobsAdmin,
  type ActiveFilter,
  type CannedJobAdminItem,
} from '@/hooks/cannedJobs/useCannedJobsAdmin';
import { useCreateCannedJob } from '@/hooks/cannedJobs/useCreateCannedJob';
import { CannedJobEditor } from '@/components/cannedJobs/CannedJobEditor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, ShieldAlert, FileStack } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FavoriteToggle } from '@/components/cannedJobs/FavoriteToggle';

export default function CannedJobsPage() {
  const { activeShop } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const shopId = activeShop?.id;

  const allowed = role === 'OWNER' || role === 'MANAGER';

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('__all__');
  const [active, setActive] = useState<ActiveFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: jobs = [], isLoading } = useCannedJobsAdmin(shopId, {
    search,
    category: category === '__all__' ? null : category,
    active,
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) if (j.category) set.add(j.category);
    return Array.from(set).sort();
  }, [jobs]);

  const selected = jobs.find((j) => j.id === selectedId) ?? null;

  if (roleLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="container mx-auto p-6 max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Access restricted
            </CardTitle>
            <CardDescription>
              Canned job templates can only be managed by Owners and Managers.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <FileStack className="h-5 w-5" /> Canned Jobs
          </h1>
          <p className="text-sm text-muted-foreground">
            Reusable service templates that expand into ticket parts and labor.
          </p>
        </div>
        <NewCannedJobButton
          shopId={shopId ?? ''}
          onCreated={(id) => setSelectedId(id)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-4">
        {/* Left list */}
        <Card>
          <CardHeader className="space-y-2">
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex gap-2">
              <Select value={active} onValueChange={(v) => setActive(v as ActiveFilter)}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-340px)] min-h-[300px]">
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-sm text-muted-foreground p-6 text-center">
                  No canned jobs match the current filters.
                </div>
              ) : (
                <ul className="divide-y">
                  {jobs.map((j) => (
                    <CannedJobRow
                      key={j.id}
                      job={j}
                      shopId={shopId ?? ''}
                      selected={selectedId === j.id}
                      onSelect={() => setSelectedId(j.id)}
                    />
                  ))}
                </ul>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right editor */}
        <div>
          {selected ? (
            <CannedJobEditor job={selected} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">No canned job selected</CardTitle>
                <CardDescription>
                  Select a canned job from the list, or create a new one.
                </CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function CannedJobRow({
  job,
  shopId,
  selected,
  onSelect,
}: {
  job: CannedJobAdminItem;
  shopId: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'w-full text-left px-3 py-2.5 hover:bg-accent transition-colors',
          selected && 'bg-accent',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate">{job.name}</span>
              <Badge
                variant={job.is_active ? 'default' : 'secondary'}
                className="text-[10px]"
              >
                {job.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {job.category && (
                <Badge variant="outline" className="text-[10px] uppercase">
                  {job.category}
                </Badge>
              )}
            </div>
            {job.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                {job.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {shopId && (
              <FavoriteToggle
                shopId={shopId}
                cannedJobId={job.id}
                isFavorite={job.is_favorite}
                size={14}
              />
            )}
            <Badge variant="secondary" className="text-[10px]">
              {job.line_count} line{job.line_count === 1 ? '' : 's'}
            </Badge>
          </div>
        </div>
      </button>
    </li>
  );
}

function NewCannedJobButton({
  shopId,
  onCreated,
}: {
  shopId: string;
  onCreated: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  const createMut = useCreateCannedJob();

  const reset = () => {
    setName('');
    setDescription('');
    setCategory('');
  };

  const handleCreate = async () => {
    if (!shopId || !name.trim()) return;
    try {
      const id = await createMut.mutateAsync({
        shopId,
        name: name.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
      });
      onCreated(id);
      setOpen(false);
      reset();
    } catch {
      // toast in hook
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={!shopId}>
        <Plus className="h-4 w-4 mr-1" /> New Canned Job
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Canned Job</DialogTitle>
            <DialogDescription>
              Create a draft canned job. You can add lines and activate it next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="new-cj-name">Name *</Label>
              <Input
                id="new-cj-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Front Brake Service"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-cj-category">Category</Label>
              <Input
                id="new-cj-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Brakes"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-cj-desc">Description</Label>
              <Input
                id="new-cj-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || createMut.isPending}
            >
              {createMut.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
